import { Component, type ErrorInfo, type ReactNode } from 'react'

import { log, LogLevel } from '@/shared/logging'

/** Reloads the page: React keeps a lazy chunk's failure, so only a fresh load retries it. */
export function reloadPage(): void {
  window.location.reload()
}

interface ErrorBoundaryProps {
  /** What to show instead of the children once one of them has thrown. */
  readonly fallback: ReactNode
  /** What the children show, such as the address; a new value tries them again. */
  readonly resetKey?: string
  readonly children: ReactNode
}

interface ErrorBoundaryState {
  readonly failed: boolean
  readonly resetKey: string | undefined
}

/**
 * Keeps a failure inside its part of the page, such as a page or a contribution whose chunk
 * failed to load, so the rest of the app stays usable.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { failed: false, resetKey: this.props.resetKey }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { failed: true }
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    return props.resetKey === state.resetKey ? null : { failed: false, resetKey: props.resetKey }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Plain fields rather than the Error, so the component stack travels with the JavaScript one.
    log(LogLevel.Error, 'A part of the page failed to render.', {
      error: `${error.name}: ${error.message}`,
      stack: error.stack ?? null,
      componentStack: info.componentStack ?? null,
    })
  }

  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}
